import { apiClient } from '../../../shared/api/client';
import {
  AnonymousSurveyInstanceDTO,
  SurveyResponseDTO,
  CreateSurveyResponseDTO,
  SubmitAnswerDTO,
  BatchSubmitAnswersDTO,
  SurveyResponseSessionDTO,
  SurveyResponseProgressDTO,
  ResponseValidationResultDTO,
  SurveyResponseSummaryDTO,
} from '../dtos';

// Base URLs for public survey endpoints (no auth required)
const PUBLIC_BASE_URL = '/public/surveys';

/**
 * Get public survey instance data (no authentication required)
 */
export const getPublicSurveyInstance = async (
  instanceId: string,
  surveyId: string
): Promise<AnonymousSurveyInstanceDTO> => {
  // Use efficient route pattern with surveyId
  const url = `${PUBLIC_BASE_URL}/${surveyId}/instances/${instanceId}`;

  const response = await apiClient.get<AnonymousSurveyInstanceDTO>(url);
  if (!response.data) {
    throw new Error('No survey instance data received');
  }
  return response.data;
};

/**
 * Create anonymous survey response session
 */
export const createAnonymousSession = async (
  instanceId: string,
  sessionData: CreateSurveyResponseDTO,
  surveyId: string
): Promise<SurveyResponseSessionDTO> => {
  const url = `${PUBLIC_BASE_URL}/${surveyId}/instances/${instanceId}/sessions`;

  const response = await apiClient.post<SurveyResponseSessionDTO>(url, sessionData);
  if (!response.data) {
    throw new Error('No data received from API');
  }
  return response.data;
};

/**
 * Get existing session for survey instance
 */
export const getSession = async (
  instanceId: string,
  sessionId: string,
  surveyId: string
): Promise<SurveyResponseSessionDTO> => {
  const url = `${PUBLIC_BASE_URL}/${surveyId}/instances/${instanceId}/sessions/${sessionId}`;

  const response = await apiClient.get<SurveyResponseSessionDTO>(url);
  if (!response.data) {
    throw new Error('No data received from API');
  }
  return response.data;
};

/**
 * Submit answer for a single question
 */
export const submitAnswer = async (
  instanceId: string,
  sessionId: string,
  answerData: SubmitAnswerDTO,
  surveyId: string
): Promise<SurveyResponseProgressDTO> => {
  const url = `${PUBLIC_BASE_URL}/${surveyId}/instances/${instanceId}/sessions/${sessionId}/answers`;

  const response = await apiClient.post<SurveyResponseProgressDTO>(url, answerData);
  if (!response.data) {
    throw new Error('No data received from API');
  }
  return response.data;
};

/**
 * Submit multiple answers at once
 */
export const submitBatchAnswers = async (
  instanceId: string,
  sessionId: string,
  batchData: BatchSubmitAnswersDTO,
  surveyId: string
): Promise<SurveyResponseProgressDTO> => {
  const url = `${PUBLIC_BASE_URL}/${surveyId}/instances/${instanceId}/sessions/${sessionId}/answers/batch`;

  const response = await apiClient.post<SurveyResponseProgressDTO>(url, batchData);
  if (!response.data) {
    throw new Error('No data received from API');
  }
  return response.data;
};

/**
 * Complete survey response
 */
export const completeSurveyResponse = async (
  instanceId: string,
  sessionId: string,
  surveyId: string
): Promise<SurveyResponseSummaryDTO> => {
  const url = `${PUBLIC_BASE_URL}/${surveyId}/instances/${instanceId}/sessions/${sessionId}/complete`;

  const response = await apiClient.post<SurveyResponseSummaryDTO>(url);
  if (!response.data) {
    throw new Error('No data received from API');
  }
  return response.data;
};

/**
 * Get current survey response progress
 */
export const getSurveyResponseProgress = async (
  instanceId: string,
  sessionId: string,
  surveyId: string
): Promise<SurveyResponseProgressDTO> => {
  const url = `${PUBLIC_BASE_URL}/${surveyId}/instances/${instanceId}/sessions/${sessionId}/progress`;

  const response = await apiClient.get<SurveyResponseProgressDTO>(url);
  if (!response.data) {
    throw new Error('No data received from API');
  }
  return response.data;
};

/**
 * Validate survey response
 */
export const validateSurveyResponse = async (
  instanceId: string,
  sessionId: string,
  surveyId: string
): Promise<ResponseValidationResultDTO> => {
  const response = await apiClient.post<ResponseValidationResultDTO>(
    `${PUBLIC_BASE_URL}/${surveyId}/instances/${instanceId}/sessions/${sessionId}/validate`
  );
  if (!response.data) {
    throw new Error('No data received from API');
  }
  return response.data;
};

/**
 * Get current survey response data
 */
export const getSurveyResponse = async (
  instanceId: string,
  sessionId: string,
  surveyId: string
): Promise<SurveyResponseDTO> => {
  const response = await apiClient.get<SurveyResponseDTO>(
    `${PUBLIC_BASE_URL}/${surveyId}/instances/${instanceId}/sessions/${sessionId}/response`
  );
  if (!response.data) {
    throw new Error('No data received from API');
  }
  return response.data;
};

/**
 * Update survey response metadata (language, user agent, etc.)
 */
export const updateResponseMetadata = async (
  instanceId: string,
  sessionId: string,
  metadata: {
    language?: string;
    userAgent?: string;
  }
): Promise<SurveyResponseDTO> => {
  const response = await apiClient.patch<SurveyResponseDTO>(
    `${PUBLIC_BASE_URL}/instances/${instanceId}/sessions/${sessionId}/metadata`,
    metadata
  );
  if (!response.data) {
    throw new Error('No data received from API');
  }
  return response.data;
};

/**
 * Check if survey instance is valid and accessible
 */
export const checkSurveyInstanceStatus = async (
  instanceId: string
): Promise<{
  isValid: boolean;
  isExpired: boolean;
  timeRemaining?: string;
  message?: string;
}> => {
  const response = await apiClient.get<{
    isValid: boolean;
    isExpired: boolean;
    timeRemaining?: string;
    message?: string;
  }>(`${PUBLIC_BASE_URL}/instances/${instanceId}/status`);
  if (!response.data) {
    throw new Error('No data received from API');
  }
  return response.data;
};

/**
 * Ping session to keep it alive
 */
export const pingSession = async (
  instanceId: string,
  sessionId: string
): Promise<{ success: boolean; lastActiveAt: string }> => {
  const response = await apiClient.post<{ success: boolean; lastActiveAt: string }>(
    `${PUBLIC_BASE_URL}/instances/${instanceId}/sessions/${sessionId}/ping`
  );
  if (!response.data) {
    throw new Error('No data received from API');
  }
  return response.data;
};
